
import type { PermissionsState } from '@/types/permissions';
import { subscriptionReconciliationService } from '@/services/subscription/SubscriptionReconciliationService';

/**
 * Utilities for subscription-related operations
 *
 * PHASE 2.3: Enhanced with cross-platform subscription reconciliation
 */
export const subscriptionUtils = {
  /**
   * Check subscription expiry and return validated permissions
   * Enhanced with cross-platform subscription reconciliation
   */
  checkSubscriptionExpiry: (permissions: PermissionsState): PermissionsState => {
    // If user has premium but we need to check expiry
    if (permissions.isPremium && permissions.source === 'stripe') {
      // In a real implementation, you would check current_period_end from your subscription data
      // For now, we'll trust the permissions as-is since Stripe webhooks handle expiry
      return permissions;
    }

    return permissions;
  },

  /**
   * Reconcile cross-platform subscriptions for enhanced accuracy
   *
   * PHASE 2.3: NEW - Provides unified subscription status across iOS IAP and Stripe
   */
  reconcileSubscriptions: async (userId: string): Promise<{
    isActive: boolean;
    source: 'ios_iap' | 'stripe' | 'multiple' | 'none';
    primaryPlatform: string | null;
    productId: string | null;
    expiresAt: Date | null;
  }> => {
    try {
      const status = await subscriptionReconciliationService.reconcileUserSubscriptions(
        userId,
        {
          forceRefresh: false, // Use cache for performance
          validateReceipts: false, // Skip receipt validation for routine checks
          resolveConflicts: true,
          updatePermissions: false // Don't auto-update permissions here
        }
      );

      return {
        isActive: status.isActive,
        source: status.source,
        primaryPlatform: status.primaryPlatform,
        productId: status.productId,
        expiresAt: status.currentPeriodEnd
      };
    } catch (error) {
      console.error('❌ Subscription Utils: Failed to reconcile subscriptions:', error);

      // Return safe fallback
      return {
        isActive: false,
        source: 'none',
        primaryPlatform: null,
        productId: null,
        expiresAt: null
      };
    }
  },

  /**
   * Force refresh subscription status with full validation
   *
   * PHASE 2.3: NEW - Comprehensive subscription refresh with receipt validation
   */
  forceRefreshSubscriptions: async (userId: string): Promise<{
    isActive: boolean;
    source: 'ios_iap' | 'stripe' | 'multiple' | 'none';
    primaryPlatform: string | null;
    productId: string | null;
    expiresAt: Date | null;
    conflictsResolved: boolean;
  }> => {
    try {
      const status = await subscriptionReconciliationService.reconcileUserSubscriptions(
        userId,
        {
          forceRefresh: true,
          validateReceipts: true,
          resolveConflicts: true,
          updatePermissions: true
        }
      );

      return {
        isActive: status.isActive,
        source: status.source,
        primaryPlatform: status.primaryPlatform,
        productId: status.productId,
        expiresAt: status.currentPeriodEnd,
        conflictsResolved: !!status.conflictResolution
      };
    } catch (error) {
      console.error('❌ Subscription Utils: Failed to force refresh subscriptions:', error);

      // Return safe fallback
      return {
        isActive: false,
        source: 'none',
        primaryPlatform: null,
        productId: null,
        expiresAt: null,
        conflictsResolved: false
      };
    }
  }
};
